import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { ALIGNMENT, ASPECT_RATIO, TypographySize, SEMANTIC_TAG } from 'mm-theme-configuration/dist/consts';
import { CardOverlay } from '../../CardOverlay';
import { CardBorder } from '../../CardBorder';
import { Image } from '../../image/Image';
import { CardCaptionContainer, CARD_CAPTION_CONTAINER_TYPES } from '../../CardCaptionContainer';
import { useTheme } from '../../../../theming/useTheme';
import { PlayIcon } from '../../PlayIcon';
import { WrapperForSafariBorderRadiusBug } from '../WrapperForSafariBorderRadiusBug';
import { Thumbnail } from '../../image/image.types';
import { Link } from '../../link/Link';
import { H3 } from '../../../../typography/semanticTags';
import { createStyleForBoxShadow } from '../../../../theming/boxShadow';

const numberOfLines = 2;
export const componentConfigThemeKey = 'onTopCard';

export interface OnTopCardComponentDataProps {
  title: string
  image: Thumbnail
  coverType?: string
  articleUrl: string
  lazyLoad?: boolean
}

export interface OnTopCardComponentThemeProps {
  cardTitleColorTypeNormal: string
  cardTitleColorTypeHover: string
  cardTitleColorTypeActive: string
  cardTextTagName: string
  borderRadius: number
  boxShadowTypeNormal: string
  boxShadowTypeActive: string
  boxShadowTypeHover: string
  borderWidthTypeNormal: number
  borderWidthTypeHover: number
  borderWidthTypeActive: number
  borderColorTypeNormal: string
  borderColorTypeHover: string
  borderColorTypeActive: string
  aspectRatioLarge: ASPECT_RATIO,
  aspectRatioMedium: ASPECT_RATIO,
  aspectRatioSmall: ASPECT_RATIO,
  overlayColor: string,
  titleFontSizeLarge: TypographySize,
  titleFontSizeMedium: TypographySize,
  titleFontSizeSmall: TypographySize,
  titleAlignment: ALIGNMENT,
  showDate: boolean,
  dateFontSizeLarge: TypographySize,
  dateFontSizeMedium: TypographySize,
  dateFontSizeSmall: TypographySize,
  dateColor: string,
  pipeColor: string,
  showAuthor: boolean,
  authorFontSizeLarge: TypographySize,
  authorFontSizeMedium: TypographySize,
  authorFontSizeSmall: TypographySize,
  authorColor: string,
  wrapperGapLarge: number,
  wrapperGapMedium: number,
  wrapperGapSmall: number,
  verticalGapLarge: number,
  verticalGapMedium: number,
  verticalGapSmall: number,
  horizontalGapLarge: number,
  horizontalGapMedium: number,
  horizontalGapSmall: number,
  titleSemanticTag: SEMANTIC_TAG,
  dateSemanticTag: SEMANTIC_TAG,
  authorSemanticTag: SEMANTIC_TAG
}


type OnTopCardComponentProps = OnTopCardComponentDataProps & OnTopCardComponentThemeProps;
const createCardStyles = (cardTextColorTypeNormal: string, cardTextColorTypeHover: string, cardTextColorTypeActive: string, borderRadius: number) => StyleSheet.create({
  card: {
    width: '100%',
    height: '100%',
    position: 'relative',
    textDecoration: 'none',
    color: cardTextColorTypeNormal,
    ':hover': {
      color: cardTextColorTypeHover,
    },
    ':active': {
      color: cardTextColorTypeActive,
    },
    display: 'block',
    '-webkit-mask-image': '-webkit-radial-gradient(white, black)',
    borderRadius: `${borderRadius}px`,
  },
});


export const OnTopCardComponent: React.FunctionComponent<OnTopCardComponentProps> = props => {
  const {
    title,
    image,
    articleUrl,
    coverType,
    cardTitleColorTypeNormal,
    cardTitleColorTypeHover,
    cardTitleColorTypeActive,
    lazyLoad = false,
    boxShadowTypeNormal,
    boxShadowTypeActive,
    boxShadowTypeHover,
    borderRadius,
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal,
    titleFontSizeLarge,
    titleFontSizeSmall,
    titleFontSizeMedium,
    showAuthor,
    showDate,
    aspectRatioLarge,
    aspectRatioMedium,
    aspectRatioSmall,
    authorColor,
    authorFontSizeLarge,
    authorFontSizeMedium,
    authorFontSizeSmall,
    titleAlignment,
    cardTextTagName,
    dateColor,
    dateFontSizeLarge,
    dateFontSizeMedium,
    dateFontSizeSmall,
    horizontalGapLarge,
    horizontalGapMedium,
    horizontalGapSmall,
    overlayColor,
    verticalGapLarge,
    verticalGapMedium,
    verticalGapSmall,
    wrapperGapLarge,
    wrapperGapMedium,
    wrapperGapSmall,
    titleSemanticTag,
    dateSemanticTag,
    authorSemanticTag } = props;
  const cardStyles = createCardStyles(cardTitleColorTypeNormal, cardTitleColorTypeHover, cardTitleColorTypeActive, borderRadius);
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal, boxShadowTypeHover, boxShadowTypeActive });
  return (
    <WrapperForSafariBorderRadiusBug boxShadowStyle={boxShadowStyle} borderRadius={borderRadius}>
      <Link className={css(cardStyles.card, boxShadowStyle.style)} href={articleUrl}>
        <Image {...image} lazyLoad={lazyLoad} />
        <CardOverlay />
        <CardBorder
          borderColorTypeActive={borderColorTypeActive}
          borderColorTypeHover={borderColorTypeHover}
          borderColorTypeNormal={borderColorTypeNormal}
          borderWidthTypeActive={borderWidthTypeActive}
          borderWidthTypeHover={borderWidthTypeHover}
          borderWidthTypeNormal={borderWidthTypeNormal}
          borderRadius={borderRadius}
        />
        <CardCaptionContainer numberOfLines={numberOfLines} type={CARD_CAPTION_CONTAINER_TYPES.ON_TOP}>
          <H3 fontSizeLarge={titleFontSizeLarge} fontSizeMedium={titleFontSizeMedium} fontSizeSmall={titleFontSizeSmall}>{title}</H3>
        </CardCaptionContainer>
        {coverType === 'video' ? <PlayIcon /> : null}
      </Link>
    </WrapperForSafariBorderRadiusBug>
  );
};


const mapThemeProps = (theme: OnTopCardComponentThemeProps) => {
  return {
    cardTitleColorTypeNormal: theme.cardTitleColorTypeNormal,
    cardTitleColorTypeHover: theme.cardTitleColorTypeHover,
    cardTitleColorTypeActive: theme.cardTitleColorTypeActive,
    cardTextTagName: theme.cardTextTagName,
    borderRadius: theme.borderRadius,
    boxShadowTypeNormal: theme.boxShadowTypeNormal,
    boxShadowTypeActive: theme.boxShadowTypeActive,
    boxShadowTypeHover: theme.boxShadowTypeHover,
    borderWidthTypeNormal: theme.borderWidthTypeNormal,
    borderWidthTypeHover: theme.borderWidthTypeHover,
    borderWidthTypeActive: theme.borderWidthTypeActive,
    borderColorTypeNormal: theme.borderColorTypeNormal,
    borderColorTypeHover: theme.borderColorTypeHover,
    borderColorTypeActive: theme.borderColorTypeActive,
    aspectRatioLarge: theme.aspectRatioLarge,
    aspectRatioMedium: theme.aspectRatioMedium,
    aspectRatioSmall: theme.aspectRatioSmall,
    overlayColor: theme.overlayColor,
    titleFontSizeLarge: theme.titleFontSizeLarge,
    titleFontSizeMedium: theme.titleFontSizeMedium,
    titleFontSizeSmall: theme.titleFontSizeSmall,
    titleAlignment: theme.titleAlignment,
    showDate: theme.showDate,
    dateFontSizeLarge: theme.dateFontSizeLarge,
    dateFontSizeMedium: theme.dateFontSizeMedium,
    dateFontSizeSmall: theme.dateFontSizeSmall,
    dateColor: theme.dateColor,
    pipeColor: theme.pipeColor,
    showAuthor: theme.showAuthor,
    authorFontSizeLarge: theme.authorFontSizeLarge,
    authorFontSizeMedium: theme.authorFontSizeMedium,
    authorFontSizeSmall: theme.authorFontSizeSmall,
    authorColor: theme.authorColor,
    wrapperGapLarge: theme.wrapperGapLarge,
    wrapperGapMedium: theme.wrapperGapMedium,
    wrapperGapSmall: theme.wrapperGapSmall,
    verticalGapLarge: theme.verticalGapLarge,
    verticalGapMedium: theme.verticalGapMedium,
    verticalGapSmall: theme.verticalGapSmall,
    horizontalGapLarge: theme.horizontalGapLarge,
    horizontalGapMedium: theme.horizontalGapMedium,
    horizontalGapSmall: theme.horizontalGapSmall,
    titleSemanticTag: theme.titleSemanticTag,
    dateSemanticTag: theme.dateSemanticTag,
    authorSemanticTag: theme.authorSemanticTag,
  };
};

export const OnTopCard = (props: OnTopCardComponentDataProps) => {
  const themeContext = useTheme(mapThemeProps, componentConfigThemeKey);
  return <OnTopCardComponent {...themeContext} {...props} />;
};
